import type { SubscriptionFeedPage } from '../subscriptions/feed.ts';
import { mergeSubscriptionFeedRows } from '../subscriptions/feed.ts';
import { parseError, type ParsedList, type ParseError } from '../result.ts';
import type { ParseContext } from './context.ts';
import { asArray, asNonEmptyString, asObject, field } from './raw.ts';
import { parseVideoSummary } from './video.ts';

/**
 * `GET /api/v1/auth/subscriptions` → `[{author, authorId}]` and nothing else. No thumbnails, no
 * counts, no descriptions — §8's "IDs only, hydrate separately". Verified on the wire in 0g: three
 * subscriptions came back with exactly those two keys.
 *
 * **Correcting what this comment used to claim**: it said an avatar needs no hydration
 * call, "the same way history's thumbnails do not: a channel avatar is reachable from the UCID
 * alone". It is not. A *video* thumbnail is addressable from its id — `/vi/{videoId}/mqdefault.jpg`,
 * which is what makes History's grid free — but a channel avatar lives at an opaque
 * `yt3.googleusercontent.com` path that only the channel record carries, and there is no
 * UCID-addressable form of it. Measured: `/ggpht/{ucid}` is a `400`, `/vi/{ucid}/mqdefault.jpg` a
 * `404`, and `/api/v1/channels/{ucid}/thumbnail` answers **`200` with zero bytes** — the captions
 * trap in a sixth place, and the same shape 2d found for an unrecognised channel tab.
 *
 * So an avatar costs a whole `/api/v1/channels/{ucid}` call, measured at **48–52 KB** on three
 * channels, and `?fields=` does **not** trim it (byte-identical with and without, including the
 * documented nested `authorThumbnails(url,width)` form). That is what makes the hydration lazy and
 * cached rather than eager — `subscriptions/hydrate.ts` owns the decision.
 */
export interface SubscriptionRef {
  readonly channelId: string;
  readonly name: string;
}

/** R4 — one malformed entry drops itself rather than emptying the list. */
export function parseSubscriptionList(raw: unknown): ParsedList<SubscriptionRef> {
  const array = asArray(raw);
  if (array === null) {
    return { items: [], dropped: [parseError('subscription-list', null, 'wrong-type')] };
  }

  const items: SubscriptionRef[] = [];
  const dropped = [];
  for (const entry of array) {
    const source = asObject(entry);
    if (source === null) {
      dropped.push(parseError('subscription', null, 'wrong-type'));
      continue;
    }
    const channelId = asNonEmptyString(field(source, 'authorId'));
    if (channelId === null) {
      dropped.push(parseError('subscription', 'authorId', 'missing'));
      continue;
    }
    // An empty `author` is survivable — the UCID is what makes the row usable and linkable.
    items.push({ channelId, name: asNonEmptyString(field(source, 'author')) ?? '' });
  }

  return { items, dropped };
}

/**
 * `GET /api/v1/auth/feed` → `{notifications: [...], videos: [...]}`, parsed into the **merged** window
 * those two arrays were split out of. `subscriptions/feed.ts` carries the measurements that make the
 * merge the correct reading rather than a convenience; this function is only the R4 half.
 *
 * `malformed` is separated from "both arrays empty" because that emptiness is a real, reachable and
 * *frequent* answer: an account with no subscriptions, every page past the last one, and — until
 * `subscriptionFeedWireParams` clamps it — any request carrying `max_results=0`. All three are
 * byte-identical (`{"notifications":[],"videos":[]}`, 32 bytes), so the page cannot tell them apart
 * from the response and must not try; what it *can* tell apart is a response that is not this shape at
 * all, which is what this flag is for.
 *
 * A missing key is **not** malformed, deliberately. Either array absent while the other is a real list
 * is a shape this endpoint could plausibly grow into (an instance with notifications switched off),
 * and treating it as a broken response would blank a feed that arrived intact.
 */
export function parseSubscriptionFeedPage(raw: unknown, ctx: ParseContext): SubscriptionFeedPage {
  const source = asObject(raw);
  if (source === null) {
    return {
      rows: [],
      dropped: [parseError('SubscriptionFeed', null, 'wrong-type')],
      malformed: true,
    };
  }

  const notifications = field(source, 'notifications');
  const videos = field(source, 'videos');
  // Neither key present at all: an envelope this is not. One of the two missing is fine — see above.
  if (notifications === undefined && videos === undefined) {
    return {
      rows: [],
      dropped: [parseError('SubscriptionFeed', 'videos', 'missing')],
      malformed: true,
    };
  }

  const dropped: ParseError[] = [];
  const parsed = (value: unknown, key: string) => {
    if (value === undefined) return [];
    const array = asArray(value);
    if (array === null) {
      dropped.push(parseError('SubscriptionFeed', key, 'wrong-type'));
      return [];
    }
    const items = [];
    for (const entry of array) {
      const video = parseVideoSummary(entry, ctx);
      if (video.ok) items.push(video.value);
      else dropped.push(video.error);
    }
    return items;
  };

  return {
    rows: mergeSubscriptionFeedRows(parsed(notifications, 'notifications'), parsed(videos, 'videos')),
    dropped,
    malformed: false,
  };
}
